import { GwizzyCommunity } from '@common/helpers/transformGwizzyCommunity';
import { useReducerAsync } from '@react/lib/useReducerAsync';
import { GuidedFormType, NudgeType } from '@react/virtual-advisor/constants';

const TOGGLE_CHAT_OPEN = 'TOGGLE_CHAT_OPEN';
const SET_CHAT_OPEN = 'SET_CHAT_OPEN';
const SET_CHAT_CLOSE = 'SET_CHAT_CLOSE';

export const RESET = 'RESET';
export const SET_GUIDED_FLOW = 'SET_GUIDED_FLOW';
export const SET_IS_SHOWING_NUDGES = 'SET_IS_SHOWING_NUDGES';
export const SET_NUDGES_SEEN = 'SET_NUDGES_SEEN';
export const SET_AGENT = 'SET_AGENT';
export const TOGGLE_EXPANDED = 'TOGGLE_EXPANDED';
export const SET_IS_SHOWING_TOOLTIP = 'SET_IS_SHOWING_TOOLTIP';
export const SET_IS_INITIALIZED = 'SET_IS_INITIALIZED';
export const SET_IS_ENGAGED = 'SET_IS_ENGAGED';
export const SET_RECOMMENDED_COMMUNITIES = 'SET_RECOMMENDED_COMMUNITIES';
export const SET_USER_OPTED_COMMUNITIES = 'SET_USER_OPTED_COMMUNITIES';
export const SET_USER_OPTED_PRIMARY_COMMUNITY =
  'SET_USER_OPTED_PRIMARY_COMMUNITY'; // This is for the case where user has requested for tour from recommended communtites

export const initialState = {
  isChatOpen: false,
  currentGuidedFlow: '' as GuidedFormType,
  isShowingNudges: true,
  nudgesSeen: [] as NudgeType[][number][],
  agent: null,
  isExpanded: false,
  isShowingTooltip: true,
  isInitialized: false,
  isEngaged: false,
  recommendedCommunities: [] as GwizzyCommunity[],
  userOptedCommunities: [] as GwizzyCommunity[],
  userOptedPrimaryCommunity: null as GwizzyCommunity | null,
};

export const CHATBOT_ACTIONS = {
  TOGGLE_CHAT_OPEN,
  SET_CHAT_CLOSE,
  SET_CHAT_OPEN,
  RESET,
  SET_GUIDED_FLOW,
  SET_IS_SHOWING_NUDGES,
  SET_NUDGES_SEEN,
  SET_AGENT,
  TOGGLE_EXPANDED,
  SET_IS_SHOWING_TOOLTIP,
  SET_IS_INITIALIZED,
  SET_IS_ENGAGED,
  SET_RECOMMENDED_COMMUNITIES,
  SET_USER_OPTED_COMMUNITIES,
  SET_USER_OPTED_PRIMARY_COMMUNITY,
} as const;

export const reducer = (
  state: typeof initialState,
  action: {
    type:
      | typeof RESET
      | typeof TOGGLE_CHAT_OPEN
      | typeof SET_CHAT_OPEN
      | typeof SET_GUIDED_FLOW
      | typeof SET_IS_SHOWING_NUDGES
      | typeof SET_NUDGES_SEEN
      | typeof SET_AGENT
      | typeof TOGGLE_EXPANDED
      | typeof SET_IS_SHOWING_TOOLTIP
      | typeof SET_IS_INITIALIZED
      | typeof SET_IS_ENGAGED
      | typeof SET_RECOMMENDED_COMMUNITIES
      | typeof SET_USER_OPTED_COMMUNITIES
      | typeof SET_USER_OPTED_PRIMARY_COMMUNITY
      | typeof SET_CHAT_CLOSE;
    payload?: any;
  }
) => {
  switch (action.type) {
    case TOGGLE_CHAT_OPEN:
      return {
        ...state,
        isChatOpen: !state.isChatOpen,
      };

    case SET_CHAT_OPEN:
      return {
        ...state,
        isChatOpen: true,
      };

    case SET_GUIDED_FLOW: {
      return {
        ...state,
        currentGuidedFlow: action.payload,
      };
    }

    case SET_IS_SHOWING_NUDGES:
      return {
        ...state,
        isShowingNudges: action.payload,
      };

    case TOGGLE_EXPANDED:
      return {
        ...state,
        isExpanded: !state.isExpanded,
      };

    case SET_NUDGES_SEEN: {
      const nudgesSeen = new Set(state.nudgesSeen);
      nudgesSeen.add(action.payload);

      const nudgesSeenArray = Array.from(nudgesSeen);

      return {
        ...state,
        nudgesSeen: nudgesSeenArray,
      };
    }

    case SET_AGENT:
      return {
        ...state,
        agent: action.payload,
      };

    case SET_IS_SHOWING_TOOLTIP:
      return {
        ...state,
        isShowingTooltip: action.payload,
      };

    case SET_IS_INITIALIZED:
      return {
        ...state,
        isInitialized: action.payload,
      };
    case SET_IS_ENGAGED:
      return {
        ...state,
        isEngaged: true,
      };
    case SET_RECOMMENDED_COMMUNITIES:
      return {
        ...state,
        recommendedCommunities: action.payload,
      };

    case SET_USER_OPTED_COMMUNITIES:
      return {
        ...state,
        userOptedCommunities: action.payload,
      };

    case SET_USER_OPTED_PRIMARY_COMMUNITY:
      return {
        ...state,
        userOptedPrimaryCommunity: action.payload,
      };
    case SET_CHAT_CLOSE:
      return {
        ...state,
        isChatOpen: false,
      };
    case RESET:
      return initialState;

    default:
      throw new Error('no such action type');
  }
};

export default function useChatState() {
  const [state, dispatch] = useReducerAsync(
    'virtual-advisor',
    reducer,
    initialState,
    (initial = initialState) => ({
      ...initial,
    }),
    {}
  );

  return {
    state,
    dispatch,
  };
}
